/**
 * Open-Meteo Historical Weather API (https://open-meteo.com/en/docs/historical-weather-api).
 * Geprüft am 27.09.2026:
 *  - Modelle `era5_land` (0.1°, ab 1950) und `era5` (0.25°, ab 1940), stündlich,
 *    rund fünf Tage Verzug.
 *  - `temperature_2m` ist ein Momentanwert zur angegebenen Stunde (°C).
 *  - `elevation=nan` schaltet die statistische Höhenkorrektur ab und liefert
 *    die mittlere Zellhöhe; ohne Angabe korrigiert Open-Meteo mit 0.0065 K/m
 *    auf ein 90-m-Geländemodell (empirisch bestätigt, docs/datenquellen.md).
 *  - `cell_selection=land` (Standard) wählt eine Landzelle ähnlicher Höhe,
 *    `nearest` die nächstgelegene Zelle.
 *  - Freie API: 600/min, 5'000/h, 10'000/Tag; mehr als zwei Wochen Daten
 *    zählen anteilig als mehrere Aufrufe. Nur nicht-kommerziell.
 *  - Kommerziell: `customer-archive-api.open-meteo.com` mit `apikey`.
 *
 * Dieses Modul baut nur URLs und prüft Antworten; es hält keinen Zustand.
 */
import type { ModelId } from '../domain/types';

export const OPEN_METEO_ARCHIVE = 'https://archive-api.open-meteo.com/v1/archive';
export const OPEN_METEO_ARCHIVE_CUSTOMER = 'https://customer-archive-api.open-meteo.com/v1/archive';
export const TIME_MEANING = 'Momentanwert zur vollen Stunde (UTC), Open-Meteo «instant»';

/** Rand um ein Kalenderjahr, damit lokale Randtage und Nächte vollständig sind. */
export const CHUNK_PAD_DAYS = 2;

export function chunkRange(year: number): { start: string; end: string } {
  const pad = (y: number, m: number, d: number) => new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);
  return { start: pad(year, 1, 1 - CHUNK_PAD_DAYS), end: pad(year, 12, 31 + CHUNK_PAD_DAYS) };
}

/** Gewichtete Aufrufzahl nach Open-Meteo (Tage/14, mind. 1, je Standort). */
export function requestWeight(days: number, locations = 1): number {
  return Math.max(1, days / 14) * locations;
}

export function chunkWeight(): number {
  const { start, end } = chunkRange(2001);
  const days = (Date.parse(end) - Date.parse(start)) / 86_400_000 + 1;
  return requestWeight(days);
}

/** Parameter für den Rohabruf einer Zelle (ohne Höhenkorrektur). */
export function cellChunkParams(model: ModelId, lat: number, lon: number, year: number): Record<string, string> {
  const { start, end } = chunkRange(year);
  return {
    latitude: lat.toFixed(4),
    longitude: lon.toFixed(4),
    elevation: 'nan',
    cell_selection: 'nearest',
    models: model,
    hourly: 'temperature_2m',
    timezone: 'GMT',
    start_date: start,
    end_date: end,
  };
}

export interface DiscoveryPoint {
  lat: number;
  lon: number;
  elevation?: number;
}

/**
 * Zellwahl für mehrere Standorte in einem Aufruf (ein Tag Daten je Standort).
 * Mit Höhenangabe wählt `cell_selection=land` eine Zelle ähnlicher Höhe.
 */
export function discoveryParams(model: ModelId, points: DiscoveryPoint[]): Record<string, string> {
  const p: Record<string, string> = {
    latitude: points.map((x) => x.lat.toFixed(5)).join(','),
    longitude: points.map((x) => x.lon.toFixed(5)).join(','),
    cell_selection: 'land',
    models: model,
    hourly: 'temperature_2m',
    timezone: 'auto',
    start_date: '2020-07-01',
    end_date: '2020-07-01',
  };
  if (points.every((x) => x.elevation !== undefined)) p.elevation = points.map((x) => x.elevation!.toFixed(1)).join(',');
  return p;
}

export function buildUrl(base: string, params: Record<string, string>): string {
  return `${base}?${new URLSearchParams(params).toString()}`;
}

export interface OpenMeteoResponse {
  latitude: number;
  longitude: number;
  elevation: number;
  timezone: string;
  utc_offset_seconds: number;
  generationtime_ms?: number;
  hourly?: { time: string[]; temperature_2m: (number | null)[] };
  hourly_units?: Record<string, string>;
}

export class ProviderError extends Error {
  constructor(
    message: string,
    public kind: 'rate-limit' | 'invalid' | 'unavailable' | 'empty' | 'network' | 'format',
    public retryAfterSec?: number,
    public limitScope?: 'minute' | 'hour' | 'day' | 'month',
  ) {
    super(message);
  }
}

/** Ordnet eine Fehlerantwort von Open-Meteo einer Fehlerart zu. */
export function classifyError(status: number, body: string, retryAfter: string | null): ProviderError {
  let reason = body;
  try {
    const j = JSON.parse(body);
    if (j && typeof j.reason === 'string') reason = j.reason;
  } catch {
    /* Rohtext behalten */
  }
  const ra = retryAfter !== null && Number.isFinite(Number(retryAfter)) ? Number(retryAfter) : undefined;
  if (status === 429) {
    const scope = /minut/i.test(reason) ? 'minute' : /hour|stünd/i.test(reason) ? 'hour' : /daily|day/i.test(reason) ? 'day' : /month/i.test(reason) ? 'month' : undefined;
    const fallback = scope === 'minute' ? 65 : scope === 'hour' ? 3600 : scope === 'day' ? 6 * 3600 : 120;
    return new ProviderError(`Abruflimit des Datenanbieters erreicht: ${reason}`, 'rate-limit', ra ?? fallback, scope);
  }
  if (status === 400 || status === 401 || status === 403 || status === 404) {
    return new ProviderError(`Anfrage abgelehnt (${status}): ${reason}`, 'invalid');
  }
  return new ProviderError(`Datenanbieter nicht verfügbar (${status}): ${reason.slice(0, 200)}`, 'unavailable', ra);
}

export function validateHourly(r: unknown): OpenMeteoResponse {
  const o = r as OpenMeteoResponse;
  if (!o || typeof o !== 'object' || typeof o.latitude !== 'number' || typeof o.longitude !== 'number') {
    throw new ProviderError('Antwort ohne Koordinaten', 'format');
  }
  if (!o.hourly || !Array.isArray(o.hourly.time) || !Array.isArray(o.hourly.temperature_2m)) {
    throw new ProviderError('Antwort ohne Stundenwerte', 'empty');
  }
  if (o.hourly.time.length === 0) throw new ProviderError('Leere Zeitreihe', 'empty');
  if (o.hourly_units && o.hourly_units.temperature_2m && o.hourly_units.temperature_2m !== '°C') {
    throw new ProviderError(`Unerwartete Einheit ${o.hourly_units.temperature_2m}`, 'format');
  }
  return o;
}
